import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Rocket, Shield, Users, Database, ArrowRight, CheckCircle2, Globe, Palette, CreditCard, Zap, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { usePermissions } from '../hooks/usePermissions';
import { useTenant } from '../contexts/TenantContext';
import { useCreateNode, useCheckNodeName, checkNodeNameAvailable } from '../hooks/useNode';
import { useCurrentUser } from '../contexts/AuthContext';
import { DEFAULT_ENABLED_MODULES } from '../constants';
import { toast } from 'react-hot-toast';

export const CreateNodePage = () => {
  const { memberships, isSignedIn, isBackendSynced, isSyncing, isSyncFailed, retrySync } = useCurrentUser();
  const { isAdmin } = usePermissions();
  const { brand, isUniversal, switchOrg } = useTenant();
  const createNode = useCreateNode();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(() => {
    try {
      const pending = localStorage.getItem('pending_hub_creation');
      if (pending) {
        const parsed = JSON.parse(pending);
        return {
          name: parsed.name || '',
          slug: parsed.slug || '',
          tier: parsed.tier || 'LOCAL',
          template: parsed.template || 'CLUB',
          operations: 'comms',
          visibility: parsed.visibility || 'PUBLIC'
        };
      }
    } catch {}
    const leadOrg = typeof localStorage !== 'undefined' ? localStorage.getItem('orgsets_lead_org') || '' : '';
    return {
      name: leadOrg,
      slug: '',
      tier: 'LOCAL',
      template: 'CLUB',
      operations: 'comms',
      visibility: 'PUBLIC'
    };
  });

  const { data: isNameAvailable, isLoading: isCheckingName } = useCheckNodeName(formData.name);

  const tiers = [
    { id: 'LOCAL', name: 'Local', price: '$0', features: ['5% Platform Fee', 'All Core Modules', '1 Level Depth', '100MB Vault'] },
    { id: 'FOUNDATION', name: 'Foundation', price: '$29/mo', features: ['2% Platform Fee', '+ Local features', '1GB Vault', '250 Member Roster'] },
    { id: 'MOMENTUM', name: 'Momentum', price: '$79/mo', features: ['0% Platform Fee', '+ Foundation features', '5GB Vault', '2 Levels Depth'] }
  ];

  if (!isSignedIn) {
    return <Navigate to="/sign-in" state={{ from: '/create-org' }} replace />;
  }

  if (!isAdmin && !isUniversal && memberships.length > 0) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleFinalize = async () => {
    if (!isBackendSynced) {
      toast.error('User account must be synchronized with server before creating an organization.');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Organization name cannot be empty.');
      return;
    }

    const available = await checkNodeNameAvailable(formData.name);
    if (!available) {
      toast.error(`Organization name "${formData.name}" is already taken. Please choose another name.`);
      setStep(1);
      return;
    }

    const slug = formData.slug || formData.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    const modules = [...DEFAULT_ENABLED_MODULES];

    try {
      const created: any = await createNode.mutateAsync({
        name: formData.name,
        slug,
        template: formData.template,
        visibility: formData.visibility,
        tier: formData.tier,
        modules
      });
      // Prefer the server's slug: the backend generates it from the name and ignores the
      // client-supplied value, so the two can disagree on punctuation and spacing.
      const targetSlug = created?.slug || slug;
      localStorage.removeItem('pending_hub_creation');
      localStorage.removeItem('pending_tool_org_draft');
      switchOrg(targetSlug, '/dashboard');
    } catch (err) {
      console.error(err);
      toast.error('Failed to create organization. Please check your connection.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-12 px-4">
      <div className="mb-12 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-none bg-brand-primary text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-brand-primary/20 mb-6 font-bold text-2xl">
          <Rocket />
        </div>
        <h1 className="text-3xl font-black text-gray-900 dark:text-gray-100 tracking-tight">Create Your Organization</h1>
        <p className="text-gray-500 mt-2 font-mono uppercase tracking-widest text-[10px]">Step {step} of 4 • Organization Setup</p>
      </div>

      <div className="bg-white rounded-[2rem] border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] overflow-hidden dark:bg-slate-900">
        <div className="flex border-b border-gray-50 overflow-x-auto">
          {[1, 2, 3, 4].map((s) => (
            <div 
              key={s} 
              className={`flex-1 min-w-[80px] py-4 text-center text-[10px] font-bold uppercase tracking-widest transition-all ${
                s === step ? 'text-brand-primary border-b-2 border-brand-primary bg-brand-primary/5' : 'text-gray-300'
              }`}
            >
              Step {s}
            </div>
          ))}
        </div>

        <div className="p-8 md:p-12 min-h-[450px]">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div 
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Define Your Space</h2>
                  <div className="space-y-4">
                      <div className="space-y-2">
                          <label className="text-xs font-bold uppercase tracking-widest text-gray-400 font-mono">Organization Name</label>
                          <Input 
                            placeholder="e.g. Springfield United"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                          />
                          {formData.name.trim().length >= 2 && (
                            <div className="flex items-center gap-2 text-xs font-mono pt-1">
                              {isCheckingName ? (
                                <span className="text-gray-500">Checking availability...</span>
                              ) : isNameAvailable === false ? (
                                <span className="text-red-600 font-bold flex items-center gap-1">
                                  <X size={14} /> Name is already taken. Please choose another.
                                </span>
                              ) : isNameAvailable === true ? (
                                <span className="text-emerald-600 font-bold flex items-center gap-1">
                                  <CheckCircle2 size={14} /> Name is available!
                                </span>
                              ) : null}
                            </div>
                          )}
                      </div>
                      <div className="space-y-2">
                          <label className="text-xs font-bold uppercase tracking-widest text-gray-400 font-mono">Operations Mode</label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <button
                              onClick={() => setFormData({...formData, operations: 'finance'})}
                              className={`p-4 rounded-none border-2 border-black dark:border-white text-left transition-all ${formData.operations === 'finance' ? 'border-brand-primary bg-brand-primary/5' : 'border-black dark:border-white hover:border-black dark:border-white'}`}
                            >
                              <div className="flex items-center gap-3 mb-2">
                                <CreditCard size={18} className={formData.operations === 'finance' ? 'text-brand-primary' : 'text-gray-400'} />
                                <span className="font-bold text-sm">Finance & Ledger</span>
                              </div>
                              <p className="text-xs text-gray-500">Enable dues, fundraising, and transparent financial tracking.</p>
                            </button>
                            <button
                              onClick={() => setFormData({...formData, operations: 'comms'})}
                              className={`p-4 rounded-none border-2 border-black dark:border-white text-left transition-all ${formData.operations === 'comms' ? 'border-brand-primary bg-brand-primary/5' : 'border-black dark:border-white hover:border-black dark:border-white'}`}
                            >
                              <div className="flex items-center gap-3 mb-2">
                                <Zap size={18} className={formData.operations === 'comms' ? 'text-brand-primary' : 'text-gray-400'} />
                                <span className="font-bold text-sm">Pure Connection</span>
                              </div>
                              <p className="text-xs text-gray-500">Focus on communication, events, and community pulse.</p>
                            </button>
                          </div>
                      </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div 
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Select Capability Tier</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Choose the governance depth you need.</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tiers.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setFormData({...formData, tier: t.id})}
                        className={`p-6 rounded-[2rem] border-2 border-black dark:border-white text-left flex flex-col h-full transition-all relative ${
                          formData.tier === t.id 
                            ? 'border-brand-primary bg-brand-primary/5 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-brand-primary/10' 
                            : 'border-black dark:border-white hover:border-brand-primary/30'
                        }`}
                      >
                        {formData.tier === t.id && (
                          <div className="absolute top-4 right-4 text-brand-primary">
                            <CheckCircle2 size={24} fill="currentColor" className="text-white fill-brand-primary" />
                          </div>
                        )}
                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 font-mono mb-2">{t.name}</p>
                        <div className="flex items-baseline gap-1 mb-6">
                          <span className="text-2xl font-black text-gray-900 dark:text-white">{t.price}</span>
                        </div>
                        <ul className="space-y-3">
                          {t.features.map((f, i) => (
                            <li key={i} className="flex items-start gap-2 text-[10px] text-gray-600">
                              <CheckCircle2 size={12} className="text-green-500 mt-0.5 shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div 
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Identity Shield</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 font-mono uppercase tracking-tight">Requirement: Anti-Abuse Linkage</p>
                  
                  <div className="bg-white rounded-none p-8 border-2 border-black dark:border-white text-center space-y-6 dark:bg-slate-950">
                    <div className="w-16 h-16 bg-white rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] mx-auto flex items-center justify-center text-brand-primary dark:bg-slate-900">
                      <Shield size={32} />
                    </div>
                    <div className="max-w-md mx-auto">
                      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        To prevent network spam, {brand?.name} requires organization creators to link a verified identity provider. 
                      </p>
                    </div>

                    <div className="py-4">
                      {isBackendSynced ? (
                        <div className="bg-green-50 text-green-700 px-6 py-4 rounded-none flex items-center justify-center gap-3 border-2 border-black dark:border-white border-green-100">
                          <CheckCircle2 size={20} />
                          <span className="text-xs font-bold uppercase tracking-widest">Provisioning Rights Verified & Synced</span>
                        </div>
                      ) : isSyncing ? (
                        <div className="bg-blue-50 text-blue-700 px-6 py-4 rounded-none flex items-center justify-center gap-3 border-2 border-black dark:border-white">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs font-bold uppercase tracking-widest">Synchronizing Account Record...</span>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="bg-amber-50 text-amber-800 p-4 border-2 border-black text-xs text-left">
                            Your account is not yet confirmed in the backend database. Account sync is required to prevent orphaned organizations without owners.
                          </div>
                          <button 
                            onClick={() => retrySync(false)}
                            className="px-8 py-4 bg-white border-2 border-black dark:border-white rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] hover:border-brand-primary flex items-center justify-center gap-3 transition-all mx-auto w-full max-w-sm dark:bg-slate-900"
                          >
                            <Globe size={20} className="text-gray-400" />
                            <span className="text-xs font-bold uppercase tracking-widest text-gray-600">Retry Account Sync</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div 
                key="step4"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center space-y-8 py-12"
              >
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-none flex items-center justify-center">
                  <CheckCircle2 size={40} />
                </div>
                <div className="text-center">
                  <h2 className="text-2xl font-black text-gray-900 dark:text-gray-100 mb-2">Roster Locked</h2>
                  <p className="text-gray-500 max-w-sm">Ready to provision <span className="text-brand-primary font-bold">{formData.name}</span>.</p>
                </div>
                <div className="w-full max-w-md bg-white p-6 rounded-none border-2 border-black dark:border-white space-y-4 dark:bg-slate-950">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-400 uppercase font-mono">Operations</span>
                    <span className="font-bold text-gray-700">{formData.operations === 'finance' ? 'Finance Enabled' : 'Communication Only'}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-400 uppercase font-mono">Tier Billing</span>
                    <span className="font-bold text-brand-primary">{formData.tier === 'LOCAL' ? 'Free Portal' : 'Premium Workspace'}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="p-8 border-t border-gray-50 bg-white/50 flex items-center justify-between dark:bg-slate-950">
          <button 
            onClick={() => setStep(prev => Math.max(1, prev - 1))}
            disabled={step === 1}
            className={`px-8 py-4 rounded-none text-xs font-bold uppercase tracking-widest transition-all ${
              step === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-white dark:bg-slate-800'
            }`}
          >
            Go Back
          </button>
          <button 
            onClick={() => {
              if (step === 3 && !isBackendSynced) return;
              if (step === 4) {
                handleFinalize();
              } else {
                setStep(prev => prev + 1);
              }
            }}
            disabled={(step === 3 && !isBackendSynced) || (step === 1 && (!formData.name.trim() || isCheckingName || isNameAvailable === false))}
            className={`px-8 py-4 rounded-none text-xs font-bold uppercase tracking-widest shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition-all flex items-center gap-2 ${
              ((step === 3 && !isBackendSynced) || (step === 1 && (!formData.name.trim() || isCheckingName || isNameAvailable === false)))
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none' 
                : 'bg-navy-900 text-white shadow-navy-900/20 hover:bg-navy-800'
            }`}
          >
            {step === 4 ? 'Initiate Provisioning' : 'Next Step'}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};
